/**
 * Badge helpers for the NCR Edit workflow tab row (Collection / Credit /
 * Replacement). Map each sub-process status to the badge shown next to its
 * tab so the tab label tracks the breadcrumb stepper inside. Extracted from
 * NCREdit.tsx to keep that page under the 500-line limit.
 */
import type { NonConformanceReport } from '../../lib/api';

export type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

export interface TabBadge {
  label: string;
  variant: BadgeVariant;
}

/** Collection sub-process status → tab badge. */
export function collectionBadgeFor(ncr: NonConformanceReport): TabBadge {
  if (ncr.collection_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.collection_process?.status;
  if (!status) return { label: 'Not Started', variant: 'secondary' };
  switch (status) {
    case 'requested': return { label: 'Requested', variant: 'secondary' };
    case 'pending':   return { label: 'Pending', variant: 'secondary' };
    case 'confirmed': return { label: 'Confirmed', variant: 'outline' };
    case 'completed': return { label: 'Done', variant: 'default' };
    default:          return { label: 'Pending', variant: 'secondary' };
  }
}

/** Credit sub-process status → tab badge. */
export function creditBadgeFor(ncr: NonConformanceReport): TabBadge {
  if (ncr.credit_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.credit_process?.status;
  if (!status) return { label: 'Not Started', variant: 'secondary' };
  switch (status) {
    case 'requested':    return { label: 'Requested', variant: 'secondary' };
    case 'under_review': return { label: 'Under Review', variant: 'secondary' };
    case 'approved':     return { label: 'Approved', variant: 'outline' };
    case 'rejected':     return { label: 'Rejected', variant: 'destructive' };
    case 'processed':    return { label: 'Done', variant: 'default' };
    default:             return { label: 'Pending', variant: 'secondary' };
  }
}

/**
 * Replacement sub-process status → tab badge — mirrors creditBadgeFor
 * (a claim is resolved by either a credit or a replacement).
 */
export function replacementBadgeFor(ncr: NonConformanceReport): TabBadge {
  if (ncr.replacement_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.replacement_process?.status;
  if (!status) return { label: 'Not Started', variant: 'secondary' };
  switch (status) {
    case 'requested':  return { label: 'Requested', variant: 'secondary' };
    case 'dispatched': return { label: 'Dispatched', variant: 'outline' };
    case 'approved':   return { label: 'Approved', variant: 'outline' };
    case 'rejected':   return { label: 'Rejected', variant: 'destructive' };
    case 'completed':  return { label: 'Done', variant: 'default' };
    default:           return { label: 'Pending', variant: 'secondary' };
  }
}
